import { createFileRoute, Link } from "@tanstack/react-router";
import portrait1 from "@/assets/size-guide-1.jpg";
import portrait2 from "@/assets/size-guide-2.jpg";
import portrait3 from "@/assets/size-guide-3.jpg";

export const Route = createFileRoute("/size-guide")({
  head: () => ({
    meta: [
      { title: "Wig Cap Size Guide | European Wig Luxury" },
      {
        name: "description",
        content:
          "Head circumference guide for our hand-tied European hair wigs. Learn how to measure and choose the right cap size — Small, Medium, or Large.",
      },
      { property: "og:title", content: "Wig Cap Size Guide | European Wig Luxury" },
      {
        property: "og:description",
        content:
          "How to measure your head and pick the right wig cap size. All current pieces ship in Medium.",
      },
    ],
  }),
  component: SizeGuidePage,
});

type Size = {
  name: string;
  inches: string;
  cm: string;
  fit: string;
  photo: string;
};

const sizes: Size[] = [
  {
    name: "Small",
    inches: "21–21.5\"",
    cm: "53–55 cm",
    fit: "Narrow / petite head — snug, secure fit",
    photo: portrait1,
  },
  {
    name: "Medium / Average",
    inches: "22–22.5\"",
    cm: "56–57 cm",
    fit: "Most common — balanced, standard fit",
    photo: portrait2,
  },
  {
    name: "Large",
    inches: "23–23.5\"",
    cm: "58–60 cm",
    fit: "Roomier — comfortable for larger heads",
    photo: portrait3,
  },
];


function PortraitFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative mx-auto flex h-56 w-56 items-center justify-center">
      {/* Dashed circumference ring */}
      <svg
        viewBox="0 0 200 200"
        className="absolute inset-0 h-full w-full text-bronze/50"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.75"
        strokeDasharray="1.5 5"
      >
        <circle cx="100" cy="100" r="98" />
      </svg>
      {/* Compass tick marks */}
      {Array.from({ length: 24 }).map((_, i) => {
        const deg = i * 15;
        const major = i % 6 === 0;
        return (
          <span
            key={i}
            className={major ? "absolute w-px bg-bronze/70" : "absolute w-px bg-bronze/40"}
            style={{
              height: major ? 10 : 5,
              transform: `rotate(${deg}deg) translateY(-104px)`,
            }}
          />
        );
      })}
      {/* Photo in circle */}
      <div className="h-44 w-44 overflow-hidden rounded-full ring-1 ring-bronze/20 shadow-[inset_0_0_20px_rgba(0,0,0,0.06)]">
        <img
          src={src}
          alt={alt}
          width={352}
          height={352}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
}



function SizeGuidePage() {
  return (
    <div className="bg-ivory">
      <section className="container mx-auto max-w-6xl px-6 py-16 md:py-24">
        <div className="mb-14 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-bronze">Fit & Sizing</p>
          <h1 className="mt-3 font-display text-4xl font-medium text-espresso md:text-5xl">
            Wig Cap Sizes
          </h1>
          <p className="mt-2 font-display italic text-muted-foreground">
            Head Circumference Guide
          </p>
          <div className="mx-auto mt-6 h-px w-24 bg-bronze/40" />
          <p className="mx-auto mt-6 max-w-2xl text-sm text-muted-foreground">
            Every piece in our current collection is a <strong className="text-foreground">Medium</strong>, our
            most common size. Each cap includes an adjustable strap for a refined fit.
          </p>
        </div>

        {/* Three-head diagram */}
        <div className="grid gap-10 md:grid-cols-3">
          {sizes.map((s) => (
            <div key={s.name} className="rounded-2xl border border-border/60 bg-card p-8 text-center shadow-sm">
              <PortraitFrame src={s.photo} alt={`${s.name} cap size — portrait reference`} />
              <h2 className="mt-6 font-display text-2xl text-espresso">{s.name}</h2>
              <p className="mt-4 text-sm text-foreground">
                {s.inches} <span className="text-muted-foreground">/ {s.cm}</span>
              </p>
              <p className="mt-1 text-xs uppercase tracking-wider text-bronze">
                Circumference
              </p>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="mt-16">
          <h3 className="text-center font-display text-2xl text-espresso">
            Cap Size Comparison
          </h3>
          <div className="mx-auto mt-3 h-px w-16 bg-bronze/40" />
          <div className="mt-8 overflow-hidden rounded-2xl border border-border/60 bg-card">
            <table className="w-full text-left text-sm">
              <thead className="bg-champagne/60">
                <tr>
                  <th className="px-5 py-4 font-display text-espresso">Cap Size</th>
                  <th className="px-5 py-4 font-display text-espresso">Inches</th>
                  <th className="px-5 py-4 font-display text-espresso">Centimeters</th>
                  <th className="px-5 py-4 font-display text-espresso">Recommended Fit</th>
                </tr>
              </thead>
              <tbody>
                {sizes.map((s, i) => (
                  <tr key={s.name} className={i % 2 ? "bg-ivory" : "bg-card"}>
                    <td className="px-5 py-4 font-medium text-espresso">{s.name}</td>
                    <td className="px-5 py-4 text-foreground">{s.inches}</td>
                    <td className="px-5 py-4 text-foreground">{s.cm}</td>
                    <td className="px-5 py-4 text-muted-foreground">{s.fit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tips */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <p className="font-display text-lg text-espresso">How to measure</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Wrap a flexible measuring tape around your head, just above the ears and across
              the mid-forehead — the widest point. Keep the tape snug but not tight.
            </p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <p className="font-display text-lg text-espresso">Between two sizes?</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose the larger size for added comfort and stretch. For a fully custom fit,{" "}
              <Link to="/contact" className="underline">get in touch</Link>.
            </p>
          </div>
        </div>

        <p className="mt-10 text-center text-xs italic text-muted-foreground">
          All measurements refer to head circumference. For best fit, measure your head and
          compare to the chart above.
        </p>
      </section>
    </div>
  );
}
